import { DOCUMENT } from '@angular/common';
import { ComponentRef, Inject, Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, DetachedRouteHandle, Route, Router } from '@angular/router';

/** Scroll offsets of one scrolled element (or the window when `element` is null). */
interface ScrollPosition {
  element: Element | null;
  top: number;
  left: number;
}

interface CachedPage {
  handle: DetachedRouteHandle;
  routeConfig: Route | null;
  scroll: ScrollPosition[];
}

/**
 * Keeps pages alive after the user leaves them, and hands them back when the user returns with
 * the browser's back/forward buttons (or `location.back()`). The page reappears exactly as it was
 * (results, filters, paging, scroll position) without re-running ngOnInit, so nothing is
 * re-fetched from the server.
 *
 * Every other navigation (menu, links, `router.navigate`) builds a fresh page and throws away
 * any cached copy of it, so those still load current data.
 */
@Injectable({ providedIn: 'root' })
export class PageCacheStrategy extends BaseRouteReuseStrategy {
  private static readonly MAX_CACHED_PAGES = 10;
  /** Top-level paths that always run from scratch. Entering one also empties the cache. */
  private static readonly UNCACHED_ROOTS = ['login', 'auth', 'logout'];

  /** Insertion-ordered, so the first key is the least recently stored page. */
  private readonly cache = new Map<string, CachedPage>();
  /** Scroll offsets taken in shouldDetach, while the leaving page is still in the DOM. */
  private pendingScroll: ScrollPosition[] = [];

  // Router depends on RouteReuseStrategy, so it is resolved lazily to avoid a DI cycle.
  constructor(private injector: Injector, @Inject(DOCUMENT) private document: Document) {
    super();
  }

  override shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isCacheable(route)) {
      return false;
    }
    this.pendingScroll = this.captureScroll();
    return true;
  }

  override store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.keyFor(route);
    if (!handle) {
      // The router has re-attached this page, so it is no longer ours to destroy.
      this.cache.delete(key);
      return;
    }
    this.drop(key);
    this.cache.set(key, { handle, routeConfig: route.routeConfig, scroll: this.pendingScroll });
    this.pendingScroll = [];
    while (this.cache.size > PageCacheStrategy.MAX_CACHED_PAGES) {
      this.drop(this.cache.keys().next().value!);
    }
  }

  override shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isCacheable(route)) {
      if (route.component) {
        // Login, customer selection and logout are session boundaries.
        this.clear();
      }
      return false;
    }
    const key = this.keyFor(route);
    const page = this.cache.get(key);
    if (!page) {
      return false;
    }
    if (this.isBackForwardNavigation() && page.routeConfig === route.routeConfig) {
      return true;
    }
    this.drop(key);
    return false;
  }

  override retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isCacheable(route)) {
      return null;
    }
    const page = this.cache.get(this.keyFor(route));
    if (!page) {
      return null;
    }
    // The router inserts the page into the DOM right after this, so restore once it is there.
    setTimeout(() => this.restoreScroll(page.scroll));
    return page.handle;
  }

  /** Destroys every cached page, e.g. after a customer switch makes their data stale. */
  clear(): void {
    [...this.cache.keys()].forEach(key => this.drop(key));
  }

  private isBackForwardNavigation(): boolean {
    return this.injector.get(Router).currentNavigation()?.trigger === 'popstate';
  }

  /** Only routes that render a component are cached; lazy-loading parents have none. */
  private isCacheable(route: ActivatedRouteSnapshot): boolean {
    if (!route.component) {
      return false;
    }
    const root = route.pathFromRoot.find(r => r.url.length)?.url[0]?.path ?? '';
    return !PageCacheStrategy.UNCACHED_ROOTS.includes(root);
  }

  /** The full URL path plus sorted query params, so each distinct page gets its own entry. */
  private keyFor(route: ActivatedRouteSnapshot): string {
    const path = route.pathFromRoot.flatMap(r => r.url.map(segment => segment.path)).join('/');
    const query = Object.keys(route.queryParams).sort()
      .map(name => `${name}=${JSON.stringify(route.queryParams[name])}`)
      .join('&');
    return `${path}?${query}`;
  }

  private drop(key: string): void {
    const page = this.cache.get(key);
    this.cache.delete(key);
    (page?.handle as { componentRef?: ComponentRef<unknown> } | undefined)?.componentRef?.destroy();
  }

  /** Detaching a page from the DOM resets its scroll offsets, so record them beforehand. */
  private captureScroll(): ScrollPosition[] {
    const view = this.document.defaultView;
    const positions: ScrollPosition[] = [{ element: null, top: view?.scrollY ?? 0, left: view?.scrollX ?? 0 }];
    this.document.body.querySelectorAll('*').forEach(element => {
      if (element.scrollTop || element.scrollLeft) {
        positions.push({ element, top: element.scrollTop, left: element.scrollLeft });
      }
    });
    return positions;
  }

  private restoreScroll(positions: ScrollPosition[]): void {
    positions.forEach(({ element, top, left }) => {
      if (element) {
        element.scrollTop = top;
        element.scrollLeft = left;
      } else {
        this.document.defaultView?.scrollTo(left, top);
      }
    });
  }
}
